import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PurchaseOrderForm = () => {
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [poNumber, setPoNumber] = useState("");
  const [orderDate, setOrderDate] = useState(new Date());
  const [terms, setTerms] = useState("");
  const [status, setStatus] = useState("Pending");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");

  // Check Requisition Items Popup
  const [showReqPopup, setShowReqPopup] = useState(false);
  const [reqItems, setReqItems] = useState([]);
  const [reqSearch, setReqSearch] = useState("");
  const [reqPage, setReqPage] = useState(1);
  const [reqLoading, setReqLoading] = useState(false);

  const reqPerPage = 8;

  const navigate = useNavigate();
  const { id } = useParams();

  const user = JSON.parse(localStorage.getItem("user"));
  const createdBy = user?.id;
  const userRole = user?.role;

  const isEditable = status === "Pending" && userRole === "store_manager";

  const generatePONumber = async () => {
    try {
      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      const fyStart = month < 4 ? year - 1 : year;
      const fyEnd = fyStart + 1;

      const res = await axios.get("/purchase-orders/last-number");
      const nextNumber = res.data?.lastNumber
        ? parseInt(res.data.lastNumber) + 1
        : 1;

      const padded = String(nextNumber).padStart(4, "0");

      return `PO/${fyStart}-${String(fyEnd).slice(2)}/${padded}`;
    } catch (error) {
      console.error("Error generating PO number:", error);
      return "PO/0000-00/0000";
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get("/vendors/dropdown");

      if (Array.isArray(res.data.vendors)) {
        setVendors(res.data.vendors);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get("/items/dropdown");
      const fetchedItems = Array.isArray(res.data) ? res.data : res.data.items;

      if (Array.isArray(fetchedItems)) {
        setItems(fetchedItems);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const fetchPurchaseOrderDetails = async () => {
    if (!id) return;

    setIsSubmitting(true);

    try {
      const res = await axios.get(`/purchase-orders/${id}`);
      const poData = res.data.order;
      const fetchedPoItems = res.data.items;

      if (poData && poData.order_date) {
        setOrderDate(new Date(poData.order_date));
      }

      setPoNumber(poData.po_number);
      setTerms(poData.terms_conditions || "");
      setStatus(poData.status || "Pending");
      setExistingAttachment(
        poData.attachment_path
          ? {
              path: poData.attachment_path,
              name: poData.attachment_original_name || "Purchase Order Attachment.pdf",
            }
          : null
      );
      setAttachment(null);
      setRemoveAttachment(false);
      setAttachmentError("");

      const selected = vendors.find((v) => v.id === poData.vendor_id);

      if (selected) {
        setSelectedVendor({
          label: selected.name,
          value: selected.id,
          ...selected,
        });
        setVendorDetails(selected);
      }

      setPoItems(
        fetchedPoItems.map((item) => ({
          item_id: item.item_id,
          item_name: item.item_name || "",
          quantity: item.qty,
          rate: item.rate,
          discount_percent: item.discount_percent,
          discount_value: item.discount_total,
          gst_percent: item.gst_percent,
          gst_value: item.gst_total,
          final_total: item.final_total,
        }))
      );
    } catch (error) {
      console.error("Error fetching PO details:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchItems();
  }, []);

  useEffect(() => {
    if (id) {
      fetchPurchaseOrderDetails();
    } else {
      generatePONumber().then(setPoNumber);
    }
  }, [id, vendors]);

  const handleVendorChange = (option) => {
    if (!isEditable) return;

    setSelectedVendor(option);
    setVendorDetails(option);
  };

  const addItem = () => {
    if (!isEditable) return;

    setPoItems([
      ...poItems,
      {
        item_id: null,
        item_name: "",
        quantity: 1,
        rate: 0,
        discount_percent: 0,
        discount_value: 0,
        gst_percent: 0,
        gst_value: 0,
        final_total: 0,
      },
    ]);
  };

  const removeItem = (index) => {
    if (!isEditable) return;

    const updated = [...poItems];
    updated.splice(index, 1);
    setPoItems(updated);
  };

  const handleItemChange = (index, field, value) => {
    if (!isEditable) return;

    const updated = [...poItems];

    updated[index][field] = value;

    if (field === "item_id") {
      const selectedItem = items.find((itm) => String(itm.id) === String(value));
      updated[index].item_name = selectedItem?.name || "";
    }

    const qty = parseFloat(updated[index].quantity || 0);
    const rate = parseFloat(updated[index].rate || 0);
    const discountPercent = parseFloat(updated[index].discount_percent || 0);
    const gstPercent = parseFloat(updated[index].gst_percent || 0);

    const total = qty * rate;
    const discountValue = (total * discountPercent) / 100;
    const taxableAmount = total - discountValue;
    const gstValue = (taxableAmount * gstPercent) / 100;
    const finalTotal = taxableAmount + gstValue;

    updated[index].discount_value = discountValue;
    updated[index].gst_value = gstValue;
    updated[index].final_total = finalTotal;

    setPoItems(updated);
  };

  const calculatePoRow = (row) => {
    const qty = parseFloat(row.quantity || 0);
    const rate = parseFloat(row.rate || 0);
    const discountPercent = parseFloat(row.discount_percent || 0);
    const gstPercent = parseFloat(row.gst_percent || 0);

    const total = qty * rate;
    const discountValue = (total * discountPercent) / 100;
    const taxableAmount = total - discountValue;
    const gstValue = (taxableAmount * gstPercent) / 100;
    const finalTotal = taxableAmount + gstValue;

    return {
      ...row,
      discount_value: discountValue,
      gst_value: gstValue,
      final_total: finalTotal,
    };
  };

  const addReqItemToPO = (reqItem) => {
    if (!isEditable) return;

    const itemId = reqItem.item_id;
    // "Need" is the shortage quantity shown in the popup. Use it for PO Qty,
    // not the full outstanding requisition total.
    const qtyToAdd = parseFloat(reqItem.required_quantity || 0);

    if (!itemId || qtyToAdd <= 0) {
      alert("Invalid item or quantity.");
      return;
    }

    setPoItems((prevItems) => {
      const existingIndex = prevItems.findIndex(
        (row) => String(row.item_id) === String(itemId)
      );

      if (existingIndex !== -1) {
        const updated = [...prevItems];

        updated[existingIndex] = calculatePoRow({
          ...updated[existingIndex],
          item_id: itemId,
          item_name: reqItem.item_name,
          quantity: qtyToAdd,
        });

        return updated;
      }

      return [
        ...prevItems,
        {
          item_id: itemId,
          item_name: reqItem.item_name,
          quantity: qtyToAdd,
          rate: 0,
          discount_percent: 0,
          discount_value: 0,
          gst_percent: 0,
          gst_value: 0,
          final_total: 0,
        },
      ];
    });
  };

  const handleAttachmentChange = (event) => {
    if (!isEditable) return;

    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setAttachment(null);
      setAttachmentError("Only PDF files are allowed.");
      return;
    }

    if (file.size > 1024 * 1024) {
      setAttachment(null);
      setAttachmentError("PDF size must be 1 MB or smaller.");
      return;
    }

    setAttachment(file);
    setAttachmentError("");
  };

  const deleteExistingAttachment = () => {
    if (!isEditable) return;
    setExistingAttachment(null);
    setAttachment(null);
    setRemoveAttachment(true);
    setAttachmentError("");
  };

  const removeSelectedAttachment = () => {
    setAttachment(null);
    setAttachmentError("");
  };

  const downloadExistingAttachment = async () => {
    if (!id || !existingAttachment) return;

    try {
      const response = await axios.get(`/purchase-orders/${id}/attachment`, {
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = existingAttachment.name || "Purchase_Order_Attachment.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      alert("Failed to download PDF attachment.");
    }
  };

  const totals = poItems.reduce(
    (acc, item) => {
      const rate = parseFloat(item.rate || 0);
      const qty = parseFloat(item.quantity || 0);
      const discount = parseFloat(item.discount_value || 0);
      const gst = parseFloat(item.gst_value || 0);

      acc.subtotal += qty * rate;
      acc.discount += discount;
      acc.gst += gst;

      return acc;
    },
    { subtotal: 0, discount: 0, gst: 0 }
  );

  const grandTotal = totals.subtotal - totals.discount + totals.gst;

  const fetchRequisitionItems = async () => {
    try {
      setReqLoading(true);

      const res = await axios.get("/requisitions/check-items");

      if (Array.isArray(res.data.items)) {
        setReqItems(res.data.items);
      } else {
        setReqItems([]);
      }
    } catch (error) {
      console.error("Error fetching requisition items:", error);
      alert("Failed to fetch requisition items.");
    } finally {
      setReqLoading(false);
    }
  };

  const openReqPopup = async () => {
    setShowReqPopup(true);
    setReqPage(1);

    if (reqItems.length === 0) {
      await fetchRequisitionItems();
    }
  };

  const closeReqPopup = () => {
    setShowReqPopup(false);
  };

  const handleReqSearch = (e) => {
    setReqSearch(e.target.value);
    setReqPage(1);
  };

  const filteredReqItems = reqItems.filter((item) =>
    String(item.item_name || "")
      .toLowerCase()
      .includes(reqSearch.toLowerCase().trim())
  );

  const reqTotalPages = Math.max(
    1,
    Math.ceil(filteredReqItems.length / reqPerPage)
  );

  const reqStartIndex = (reqPage - 1) * reqPerPage;

  const paginatedReqItems = filteredReqItems.slice(
    reqStartIndex,
    reqStartIndex + reqPerPage
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditable) return;

    if (!selectedVendor) {
      alert("Please select a vendor.");
      return;
    }

    const filteredItems = poItems.filter(
      (item) =>
        item.item_id &&
        parseFloat(item.quantity) > 0 &&
        parseFloat(item.rate) > 0 &&
        parseFloat(item.final_total) > 0
    );

    if (filteredItems.length === 0) {
      return alert("Please add at least one valid item before submitting.");
    }

    const payload = {
      vendor_id: selectedVendor.id,
      po_number: poNumber,
      order_date: moment(orderDate).format("YYYY-MM-DD"),
      terms_conditions: terms,
      created_by: createdBy,
      items: filteredItems.map((item) => ({
        item_id: item.item_id,
        qty: item.quantity,
        rate: item.rate,
        discount_percent: item.discount_percent,
        discount_total: item.discount_value,
        gst_percent: item.gst_percent,
        gst_total: item.gst_value,
        total: item.quantity * item.rate,
        final_total: item.final_total,
      })),
      subtotal: totals.subtotal,
      discount_total: totals.discount,
      gst_total: totals.gst,
      grand_total: grandTotal,
    };

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("vendor_id", payload.vendor_id);
      formData.append("po_number", payload.po_number);
      formData.append("order_date", payload.order_date);
      formData.append("terms_conditions", payload.terms_conditions || "");
      formData.append("created_by", payload.created_by);
      formData.append("items", JSON.stringify(payload.items));
      formData.append("subtotal", payload.subtotal);
      formData.append("discount_total", payload.discount_total);
      formData.append("gst_total", payload.gst_total);
      formData.append("grand_total", payload.grand_total);

      if (attachment) {
        formData.append("attachment", attachment);
      }

      if (id) {
        formData.append("remove_attachment", removeAttachment ? "1" : "0");
        await axios.put(`/purchase-orders/${id}`, formData);
      } else {
        await axios.post("/purchase-orders", formData);
      }

      navigate("/dashboard/purchase-orders");
    } catch (err) {
      console.error("Submission error:", err?.response?.data || err.message);
      alert("Failed to submit purchase order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {!id && (
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn btn-warning btn-sm"
            onClick={openReqPopup}
          >
            <i className="fas fa-clipboard-check mr-1"></i>
            Check Requisition Items
          </button>
        </div>
      )}

      <h3>{id ? "Edit Purchase Order" : "Create Purchase Order"}</h3>

      <form onSubmit={handleSubmit} className="card p-4 shadow-lg">
        <div className="row">
          <div className="col-md-5 mb-3">
            <label>Vendor</label>
            <Select
              value={selectedVendor}
              options={vendors.map((v) => ({
                label: v.name,
                value: v.id,
                ...v,
              }))}
              onChange={handleVendorChange}
              isDisabled={!isEditable}
              isClearable={isEditable}
              placeholder="Select Vendor"
            />

            {vendorDetails && (
              <div className="alert alert-light border p-2 mt-2 small">
                <strong>Address:</strong> {vendorDetails.address}
                <br />
                <strong>Contact:</strong> {vendorDetails.contact_person} (
                {vendorDetails.phone})
                <br />
                <strong>GST No.:</strong> {vendorDetails.pan_gst}
                <br />
                <strong>Email:</strong> {vendorDetails.email}
              </div>
            )}
          </div>

          <div className="col-md-3 mb-3">
            <label>PO Number</label>
            <input
              type="text"
              className="form-control"
              value={poNumber}
              readOnly
            />
          </div>

          <div className="col-md-3 mb-3">
            <label>Order Date</label>
            <DatePicker
              selected={
                orderDate instanceof Date && !isNaN(orderDate)
                  ? orderDate
                  : new Date()
              }
              onChange={(date) => isEditable && setOrderDate(date)}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              readOnly={!isEditable}
              maxDate={new Date()}
              minDate={new Date()}
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Discount %</th>
                <th>Discount</th>
                <th>GST %</th>
                <th>GST</th>
                <th>Final Total</th>
                {isEditable && <th>Action</th>}
              </tr>
            </thead>

            <tbody>
              {poItems.map((item, index) => {
                const selectedItem = items.find(
                  (i) => String(i.id) === String(item.item_id)
                );

                return (
                  <tr key={index}>
                    <td style={{ minWidth: "300px" }}>
                      <Select
                        options={items.map((itm) => ({
                          label: itm.name,
                          value: itm.id,
                          isDisabled: poItems.some(
                            (row, idx) =>
                              String(row.item_id) === String(itm.id) &&
                              idx !== index
                          ),
                        }))}
                        value={
                          selectedItem
                            ? {
                                label: selectedItem.name,
                                value: item.item_id,
                              }
                            : item.item_id
                            ? {
                                label: item.item_name || `Item #${item.item_id}`,
                                value: item.item_id,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          handleItemChange(index, "item_id", selected.value)
                        }
                        isDisabled={!isEditable}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, "quantity", e.target.value)
                        }
                        disabled={!isEditable}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={item.rate}
                        onChange={(e) =>
                          handleItemChange(index, "rate", e.target.value)
                        }
                        disabled={!isEditable}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={item.discount_percent}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "discount_percent",
                            e.target.value
                          )
                        }
                        disabled={!isEditable}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={Number(item.discount_value || 0).toFixed(2)}
                        readOnly
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={item.gst_percent}
                        onChange={(e) =>
                          handleItemChange(index, "gst_percent", e.target.value)
                        }
                        disabled={!isEditable}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={Number(item.gst_value || 0).toFixed(2)}
                        readOnly
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        className="form-control"
                        value={Number(item.final_total || 0).toFixed(2)}
                        readOnly
                      />
                    </td>

                    {isEditable && (
                      <td>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeItem(index)}
                        >
                          X
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {isEditable && (
            <button
              type="button"
              className="btn btn-secondary btn-sm mb-3"
              onClick={addItem}
            >
              + Add Item
            </button>
          )}
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label>PDF Attachment <span className="text-muted">(Optional)</span></label>

            {existingAttachment && !removeAttachment && (
              <div className="border rounded p-2 mb-2 d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "8px" }}>
                <div style={{ minWidth: 0 }}>
                  <i className="fas fa-file-pdf text-danger mr-2"></i>
                  <span style={{ fontSize: "13px", wordBreak: "break-word" }}>
                    {existingAttachment.name}
                  </span>
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mr-2"
                    onClick={downloadExistingAttachment}
                  >
                    <i className="fas fa-download mr-1"></i> Download
                  </button>
                  {isEditable && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={deleteExistingAttachment}
                    >
                      <i className="fas fa-trash mr-1"></i> Delete
                    </button>
                  )}
                </div>
              </div>
            )}

            {attachment && (
              <div className="alert alert-success py-2 px-3 mb-2 d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "8px" }}>
                <span style={{ fontSize: "13px", wordBreak: "break-word" }}>
                  <i className="fas fa-file-pdf mr-2"></i>
                  New PDF: {attachment.name}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={removeSelectedAttachment}
                >
                  Remove
                </button>
              </div>
            )}

            {isEditable && (
              <input
                type="file"
                className="form-control"
                accept="application/pdf,.pdf"
                onChange={handleAttachmentChange}
              />
            )}
            <small className="text-muted d-block mt-1">One PDF only. Maximum file size: 1 MB.</small>
            {attachmentError && (
              <small className="text-danger d-block mt-1">{attachmentError}</small>
            )}
            {removeAttachment && !attachment && (
              <small className="text-warning d-block mt-1">
                Existing PDF will be deleted when you update the Purchase Order.
              </small>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-3">
            <label>Terms & Conditions</label>
            <textarea
              className="form-control"
              rows="3"
              value={terms}
              maxLength={250}
              onChange={(e) => isEditable && setTerms(e.target.value.slice(0, 250))}
              readOnly={!isEditable}
            ></textarea>
            {isEditable && (
              <small className="text-muted d-block text-right mt-1">
                {Math.min(terms.length, 250)}/250 characters
              </small>
            )}
          </div>

          <div className="col-md-3 offset-md-6">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td>{totals.subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Discount</td>
                  <td>{totals.discount.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>GST</td>
                  <td>{totals.gst.toFixed(2)}</td>
                </tr>
                <tr>
                  <th>Grand Total</th>
                  <th>{grandTotal.toFixed(2)}</th>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {isEditable && (
          <div className="text-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : id ? "Update PO" : "Create PO"}
            </button>
          </div>
        )}
      </form>

      {showReqPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "720px",
            maxWidth: "96vw",
            height: "100vh",
            background: "#ffffff",
            zIndex: 9999,
            boxShadow: "-6px 0 24px rgba(15, 23, 42, 0.22)",
            borderLeft: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid #e5e7eb",
              background: "#f8fafc",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h5 className="mb-0" style={{ fontWeight: 700 }}>
                Check Requisition Items
              </h5>
              <small className="text-muted">
                Click Add to auto-fill item and quantity in PO
              </small>
            </div>

            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary mr-2"
                onClick={fetchRequisitionItems}
                disabled={reqLoading}
              >
                <i className="fas fa-sync-alt mr-1"></i>
                Refresh
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={closeReqPopup}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div
            style={{
              padding: "12px 18px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search item name..."
              value={reqSearch}
              onChange={handleReqSearch}
            />
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "12px 18px" }}>
            {reqLoading ? (
              <div className="text-center py-5">
                <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
                <p className="mt-2 text-muted">Loading requisition items...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead style={{ background: "#f1f5f9" }}>
                    <tr>
                      <th style={{ width: "45px" }}>Sr.</th>
                      <th style={{ width: "70px" }}>Item ID</th>
                      <th>Item Name</th>
                      <th>Total Qty</th>
                      <th>Stock</th>
                      <th>Need</th>
                      <th style={{ width: "95px" }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedReqItems.length > 0 ? (
                      paginatedReqItems.map((item, index) => (
                        <tr key={item.item_id}>
                          <td>{reqStartIndex + index + 1}</td>
                          <td>{item.item_id}</td>
                          <td>
                            <strong>{item.item_name}</strong>
                          </td>
                          <td>{item.total_quantity}</td>
                          <td>{item.available_stock}</td>
                          <td>
                            <span className="badge badge-danger">
                              {item.required_quantity}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-success btn-xs"
                              onClick={() => addReqItemToPO(item)}
                              disabled={!isEditable}
                            >
                              <i className="fas fa-plus mr-1"></i>
                              Add
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          className="text-center text-muted py-4"
                        >
                          No requisition items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div
            style={{
              padding: "12px 18px",
              borderTop: "1px solid #e5e7eb",
              background: "#f8fafc",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <small className="text-muted">
              Showing {paginatedReqItems.length} of {filteredReqItems.length}{" "}
              items
            </small>

            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mr-2"
                disabled={reqPage === 1}
                onClick={() => setReqPage((prev) => Math.max(prev - 1, 1))}
              >
                Prev
              </button>

              <span style={{ fontSize: "13px", margin: "0 8px" }}>
                Page {reqPage} / {reqTotalPages}
              </span>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={reqPage === reqTotalPages}
                onClick={() =>
                  setReqPage((prev) => Math.min(prev + 1, reqTotalPages))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderForm;